import React, { useState, FC } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Portal,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core';
import { LayoutVariant } from '../../Core/CommonTypes';
import { Close } from '@material-ui/icons';

import { TabPanel } from '../../Core/Tabs/TabPanel';
import { Login } from './Login';
import { IFormSignUp, SignUp } from './SignUp';
import { useForm } from 'react-hook-form';
import Auth from '@aws-amplify/auth';
import { useAuth } from '../../../stores/AuthStore';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  title: {
    marginRight: 48
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  dialogContent: {},
  mobileDialogContent: {
    padding: theme.spacing(2),
    paddingTop: 0
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  },
  resendCode: {
    '&:hover': {
      cursor: 'pointer'
    }
  }
}));

interface AuthDialogProps {
  onClose?: () => void;
  onClick?: () => void;
  open: boolean;
  variant?: LayoutVariant;
}

export enum StateOnClose {
  SignInSuccess = 'sign_in_success'
}

const AuthDialog: FC<AuthDialogProps> = ({ onClose, variant, open }) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const [message, setMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isSuccessSnackbar, setIsSuccessSnackbar] = useState(true);
  const [currentDataSignUp, setCurrentDataSignUp] = useState<IFormSignUp>();
  const authStore = useAuth();
  const handleClose = (_event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };
  const showMessage = (m: string, isSuccess = true) => {
    setOpenSnackbar(false);
    setIsSuccessSnackbar(isSuccess);
    setMessage(m);
    setOpenSnackbar(true);
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    setError
  } = useForm<{ verifyCode: string }>();

  const onSubmit = ({ verifyCode }: { verifyCode: string }) => {
    setLoading(true);
    return Auth.confirmSignUp(currentDataSignUp?.email as string, verifyCode)
      .then(() => {
        return Auth.signIn(
          currentDataSignUp?.email as string,
          currentDataSignUp?.password as string
        )
          .then(async () => {
            await authStore.getProfile();
            showMessage('Register successfully');
            wrapperOnClose();
          })
          .catch(error => {
            setError('verifyCode', {
              message: error?.message
            });
          });
      })
      .catch(error => {
        setError('verifyCode', {
          message: error?.message
        });
      })
      .finally(() => setLoading(false));
  };
  const resend = () => {
    setLoadingResend(true);
    return Auth.resendSignUp(currentDataSignUp?.email as string)
      .then(() => {
        showMessage('Resend code successfully');
      })
      .catch(error => {
        showMessage(error?.message, false);
      })
      .finally(() => setLoadingResend(false));
  };

  const wrapperOnClose = (state?: any) => {
    if (state === StateOnClose.SignInSuccess) {
      showMessage('Login successfully');
    }
    setValue(0);
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={wrapperOnClose}
        disableBackdropClick
        fullWidth={variant === LayoutVariant.laptop}
        maxWidth="sm"
        fullScreen={variant === LayoutVariant.mobile}
        scroll="paper"
      >
        {value === -1 ? (
          <DialogTitle disableTypography>
            <Typography variant="h6" className={classes.title}>
              Verify your account
            </Typography>
            <IconButton
              aria-label="close"
              onClick={wrapperOnClose}
              className={classes.close}
            >
              <Close />
            </IconButton>
            <DialogContent>
              <Typography className={classes.title}>
                Check your email for verification code (
                <span className={classes.resendCode} onClick={resend}>
                  {loadingResend ? (
                    <CircularProgress size={16} />
                  ) : (
                    'Resend code'
                  )}
                </span>
                )
              </Typography>
              <form noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Verification Code"
                  autoFocus
                  helperText={errors?.verifyCode?.message}
                  error={!!errors?.verifyCode}
                  name="verifyCode"
                  inputRef={register({
                    required: 'This field is required'
                  })}
                />
                <Grid
                  style={{
                    marginTop: 10
                  }}
                  container
                  direction="column"
                  spacing={1}
                >
                  <Grid item>
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit(onSubmit)}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Confirm code'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </DialogContent>
          </DialogTitle>
        ) : (
          <>
            <DialogTitle disableTypography>
              <Typography variant="h6" className={classes.title}>
                Become our Member
              </Typography>
              <IconButton
                aria-label="close"
                onClick={wrapperOnClose}
                className={classes.close}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Paper className={classes.root}>
                <Tabs
                  value={value}
                  onChange={(_, v) => setValue(v)}
                  indicatorColor="primary"
                  textColor="primary"
                  centered
                >
                  <Tab label="Sign In" />
                  <Tab label="Sign up" />
                </Tabs>
              </Paper>
              <TabPanel value={value} index={0}>
                <Login onClose={wrapperOnClose} goSignUp={() => setValue(1)} />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <SignUp
                  goSignIn={data => {
                    if (data) {
                      setCurrentDataSignUp(data);
                      setValue(-1);
                    } else {
                      setValue(0);
                    }
                  }}
                />
              </TabPanel>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Portal>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={isSuccessSnackbar ? 'success' : 'error'}
          >
            {message}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default AuthDialog;
